import { useTranslation } from "react-i18next";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Target, TrendingUp } from "lucide-react";
import { formatCurrency } from "@/lib/currencyUtils";

const MarginSliderInput = ({ targetMargin, onMarginChange, totalCost, menuPrice, currency, isManualPrice }) => {
  const { t, i18n } = useTranslation();

  const handleSliderChange = (value) => {
    onMarginChange(value[0]);
  };

  const handleInputChange = (e) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value) && value >= 0 && value <= 90) {
      onMarginChange(value);
    }
  };

  const suggestedPrice = totalCost && targetMargin 
    ? (parseFloat(totalCost) / (1 - (targetMargin / 100))).toFixed(2)
    : "0.00";

  const getMarginColor = () => {
    if (targetMargin >= 70) return "text-green-500";
    if (targetMargin >= 60) return "text-amber-500";
    return "text-red-500";
  };

  const getSliderColor = () => {
    if (targetMargin >= 70) return "bg-green-500";
    if (targetMargin >= 60) return "bg-amber-500";
    return "bg-red-500";
  };

  return (
    <div className="bg-slate-900/50 rounded-lg border border-slate-800 p-6 space-y-6" data-testid="margin-slider-input">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-amber-500/10 rounded-lg">
          <Target className="h-6 w-6 text-amber-500" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-white font-manrope">
            {t("craftMargin.title")}
          </h3>
          <p className="text-sm text-slate-400">
            Dial in your target margin to see suggested pricing
          </p>
        </div>
      </div>

      {/* Total Cost Display */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-amber-500/20">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-400 mb-1">
              {t("craftMargin.theCraft")} - Base Cost
            </div>
            <div className="text-2xl font-bold text-amber-500 font-mono">
              {formatCurrency(totalCost, currency, 2, i18n.language)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-400 mb-1">
              {t("craftMargin.foodCostPercent")}
            </div>
            <div className="text-lg font-semibold text-amber-500 font-mono">
              {(100 - targetMargin).toFixed(1)}%
            </div>
          </div>
        </div>
      </div>

      {/* Margin Slider */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-green-500" />
            {t("craftMargin.theMargin")} - Target Profit
          </Label>
          <div className="flex items-center gap-3">
            <Input
              type="number"
              min="0"
              max="90"
              step="1"
              value={targetMargin.toFixed(1)}
              onChange={handleInputChange}
              className="w-20 bg-slate-800 border-slate-700 text-white font-mono text-center"
              data-testid="margin-input"
            />
            <span className="text-slate-400 font-mono">%</span>
          </div>
        </div>

        {/* Slider */}
        <div className="px-2">
          <Slider
            value={[targetMargin]}
            onValueChange={handleSliderChange}
            min={0}
            max={90}
            step={1}
            className="cursor-pointer"
            data-testid="margin-slider"
          />
          
          {/* Markers */}
          <div className="flex justify-between text-xs text-slate-500 mt-2">
            <span>0%</span>
            <span>30%</span>
            <span>60%</span>
            <span>90%</span>
          </div>
        </div>

        {/* Margin Value Display */}
        <div className="text-center">
          <div className={`text-4xl font-bold font-mono ${getMarginColor()}`}>
            {targetMargin.toFixed(1)}%
          </div>
          <div className="text-sm text-slate-400 mt-1">
            {targetMargin >= 70 && "Excellent margin"}
            {targetMargin >= 60 && targetMargin < 70 && "Good margin"}
            {targetMargin >= 40 && targetMargin < 60 && "Moderate margin"}
            {targetMargin < 40 && "Low margin"}
          </div>
        </div>
      </div>

      {/* Suggested Price Display */}
      <div className="bg-gradient-to-r from-green-500/10 to-green-600/10 rounded-lg p-4 border border-green-500/20">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-400 mb-1">
              Suggested Menu Price
              {isManualPrice && <span className="ml-2 text-amber-500">(Manual Override)</span>}
            </div>
            <div className="text-3xl font-bold text-green-500 font-mono">
              {formatCurrency(suggestedPrice, currency, 2, i18n.language)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-400 mb-1">
              {t("craftMargin.grossProfit")}
            </div>
            <div className="text-xl font-semibold text-green-400 font-mono">
              {formatCurrency((parseFloat(suggestedPrice) - parseFloat(totalCost)).toFixed(2), currency, 2, i18n.language)}
            </div>
          </div>
        </div>
      </div>

      {/* Formula Explanation */}
      <div className="bg-slate-800/30 rounded-lg p-3 border border-slate-700/50">
        <div className="text-xs text-slate-400 text-center">
          <span className="font-semibold text-slate-300">Formula:</span> Menu Price = Total Cost ÷ (1 - Margin%)
        </div>
        <div className="text-xs text-slate-500 text-center mt-1">
          Example: {formatCurrency(totalCost, currency, 2, i18n.language)} ÷ (1 - {(targetMargin/100).toFixed(2)}) = {formatCurrency(suggestedPrice, currency, 2, i18n.language)}
        </div>
      </div>
    </div>
  );
};

export default MarginSliderInput;
